/** @jsxImportSource @emotion/react */
import { useContext, useEffect } from 'react';

import useWindowSize from '@common/hooks/useWindowSize';
import { container } from '@stories/atoms/Grid';
import { zIndexes } from '@stories/atoms/utils';
import { media, mediaDown } from '@stories/theming/settings';

import { PageContext } from '../../Context/pageContext';
import Filters from '../Filters/Filters';
import Results from '../Results/Results';
import TopBar from '../TopBar/TopBar';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';

/* PRODUCT LIST
 * This is the main component of the category and search pages.
 * It features a list of products and widgets for filtering, sorting and pagination.
 * Must be rendered inside a ProductListContext Provider.
 */

const topbarCss = {
  [media('desktop')]: {
    gridArea: 'topbar'
  }
};

const filtersCss = {
  bottom: 0,
  display: 'block',
  left: 0,
  position: 'fixed',
  transform: 'translateY(100%)',
  transition: 'transform 250ms ease-out',
  width: '100vw',
  [mediaDown('desktop')]: {
    backgroundColor: 'white',
    borderRadius: '5px',
    padding: '1.5rem var(--offset)',
    width: '100vw',
    zIndex: zIndexes.darkOverlay + 1
  },
  [media('desktop')]: {
    gridArea: 'filters',
    height: 'auto',
    marginTop: '0.5rem',
    paddingRight: '6rem',
    position: 'static',
    transform: 'none',
    width: 'auto'
  }
} as const;

const ProductList: React.FC<{ cssProps?: Interpolation<Theme>; transitioning: boolean }> = ({
  cssProps,
  transitioning
}) => {
  const { isFilterOverlayShown } = useContext(PageContext);
  const { height: windowHeight } = useWindowSize();

  useEffect(() => {
    document.body.style.overflow = isFilterOverlayShown ? 'hidden' : 'auto';
  }, [isFilterOverlayShown]);

  const containerCss = {
    ...container,
    paddingTop: '.5rem',
    paddingBottom: '4rem',
    [media('desktop')]: {
      paddingTop: '0',
      display: 'grid',
      grid: `
        'topbar topbar'
        'filters results' / 18rem auto`
    }
  };

  return (
    <div css={{ ...containerCss, ...(cssProps as object) }}>
      <TopBar cssProps={topbarCss} />
      <Filters
        cssProps={{
          ...filtersCss,
          height: windowHeight !== undefined ? `${windowHeight * 0.9}px` : '100%',
          ...(isFilterOverlayShown && { transform: 'translateX(0%)' })
        }}
      />
      <Results
        cssProps={{
          opacity: 1,
          transition: 'filter 180ms ease-in-out, opacity 180ms ease-in-out',
          ...(transitioning && { filter: 'blur(5px) grayscale(60%)', opacity: 0.7 })
        }}
      />
    </div>
  );
};

export default ProductList;
